import { ContentPage } from "@/components/marketing/ContentPage";
import layout from "@/components/marketing/ContentPage.module.css";

const SECURITY_EMAIL = "security@socialmediamanager.pheonixapps.com";

export function Security() {
  return (
    <ContentPage
      kicker="Security"
      title="How we protect your data."
      lead="A plain description of the security practices in place today. We only list controls we actually run — nothing aspirational."
    >
      <h2>Authentication</h2>
      <p>
        Sign-in is handled by Amazon Cognito. The product is{" "}
        <strong>invite-only with no public signup</strong> — accounts are
        created only for people who have been invited to a workspace. Cognito
        manages credentials, password resets, and session tokens.
      </p>

      <h2>Tenant isolation</h2>
      <p>
        Every workspace's data is isolated at the data-model level. Each row in
        our database is scoped by a <code>WS#</code> key prefix that identifies
        the owning workspace, and queries are always constrained to a single
        workspace. This prevents one tenant's data from being returned to
        another.
      </p>

      <h2>Encryption</h2>
      <ul>
        <li>
          <strong>At rest.</strong> Application data is stored in Amazon
          DynamoDB and Amazon S3, both encrypted at rest by AWS.
        </li>
        <li>
          <strong>Secrets.</strong> Sensitive configuration values are stored as
          AWS Systems Manager (SSM) SecureString parameters, encrypted with AWS
          KMS rather than living in source or environment files.
        </li>
      </ul>

      <h2>Access control</h2>
      <p>
        Workspaces use role-based permissions across five roles —{" "}
        <strong>owner</strong>, <strong>admin</strong>, <strong>editor</strong>,{" "}
        <strong>approver</strong>, and <strong>viewer</strong>. These
        permissions are enforced server-side on every mutating action, not just
        hidden in the UI. A viewer can't generate, an editor can't approve, and
        so on — the server checks the caller's role before making any change.
      </p>

      <h2>Publishing safeguards</h2>
      <p>
        Every generated draft is run through server-side safety-rail checks
        before it can move to approved. A draft that trips a blocking check
        cannot be approved without an explicit override and a recorded reason
        stored alongside the draft. Nothing publishes without a person
        approving it first.
      </p>

      <h2>What we don't claim</h2>
      <p>
        We do not hold SOC 2, ISO 27001, PCI DSS, HIPAA, or any other formal
        security or compliance certification, and we don't claim to. This page
        describes the controls we run today, and we'll update it as those
        practices change.
      </p>

      <h2>Report a vulnerability</h2>
      <div className={layout.callout}>
        <svg
          className={layout.calloutIcon}
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />
          <path d="M12 8v4" />
          <path d="M12 16h.01" />
        </svg>
        <p className={layout.calloutText}>
          Found a security issue? Please email{" "}
          <a href={`mailto:${SECURITY_EMAIL}`}>{SECURITY_EMAIL}</a> with details
          and steps to reproduce. We'll acknowledge your report and keep you
          updated as we investigate.
        </p>
      </div>
    </ContentPage>
  );
}

export default Security;
